// Bell icon in the header. Polls /api/notifications every 60s.
// Click → dropdown with the latest 30; clicking a notification marks it read
// and navigates to its link.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import type { NotificationsResponse } from '../types';

const POLL_MS = 60_000;

export function NotificationBell() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<NotificationsResponse | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  const reload = useCallback(async () => {
    try {
      setData(await api<NotificationsResponse>('/api/notifications?limit=30'));
    } catch {
      // Silent — notifications aren't critical-path.
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    void reload();
    const id = setInterval(() => void reload(), POLL_MS);
    return () => clearInterval(id);
  }, [user, reload]);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    window.addEventListener('mousedown', onClick);
    return () => window.removeEventListener('mousedown', onClick);
  }, [open]);

  if (!user) return null;

  async function onClickNotification(id: string, link: string | null) {
    try { await api(`/api/notifications/${id}/read`, { method: 'POST' }); } catch {}
    setOpen(false);
    void reload();
    if (link) navigate(link);
  }

  async function markAll() {
    try { await api('/api/notifications/mark-all-read', { method: 'POST' }); } catch {}
    void reload();
  }

  const unread = data?.unreadCount ?? 0;

  return (
    <div className="notif-wrap" ref={wrapRef}>
      <button
        type="button"
        className="theme-toggle notif-button"
        aria-label={`Notifications${unread > 0 ? ` (${unread} unread)` : ''}`}
        onClick={() => setOpen((o) => !o)}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
          <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
        </svg>
        {unread > 0 && <span className="notif-badge">{unread > 99 ? '99+' : unread}</span>}
      </button>

      {open && (
        <div className="notif-dropdown" role="menu">
          <div className="notif-head">
            <span>Notifications</span>
            {unread > 0 && (
              <button type="button" className="btn-link" onClick={() => void markAll()}>
                Mark all read
              </button>
            )}
          </div>
          {!data && <div className="notif-empty">Loading…</div>}
          {data && data.notifications.length === 0 && (
            <div className="notif-empty">Nothing new.</div>
          )}
          {data && data.notifications.length > 0 && (
            <ul className="notif-list">
              {data.notifications.map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    className={`notif-item${n.isRead ? '' : ' is-unread'}`}
                    onClick={() => void onClickNotification(n.id, n.link)}
                  >
                    <strong>{n.title}</strong>
                    <span>{n.message}</span>
                    <em>{new Date(n.createdAt).toLocaleString()}</em>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
